import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SEO from "@/components/SEO";
import {
  Snowflake,
  Heart,
  BookOpen,
  Coffee,
  Utensils,
  Sparkles,
  CalendarDays,
  Check,
  ArrowRight,
  Users,
  Bed,
  Waves,
  Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROOMS = [
  {
    name: "Premium Queen Room",
    detail: "Queen bed, ensuite bathroom, private deck",
    price: "$649",
    companion: "+$300 companion rate",
    icon: Sparkles,
  },
  {
    name: "Queen Ensuite Room",
    detail: "Queen bed, ensuite bathroom",
    price: "$599",
    companion: "+$300 companion rate",
    icon: Bed,
  },
  {
    name: "Shared Bath Queen Room",
    detail: "Queen bed, shared bathroom",
    price: "$499",
    companion: "+$250 companion rate",
    icon: Bed,
  },
  {
    name: "Single Twin Room",
    detail: "One twin bed, shared bathroom",
    price: "$399",
    companion: "One guest",
    icon: Bed,
  },
  {
    name: "Two-Twin Room",
    detail: "Two twin beds, shared bathroom",
    price: "$449",
    companion: "per guest",
    icon: Users,
  },
];

const RETREATS = [
  {
    id: "january-winter-reset",
    month: "January",
    title: "Winter Reset Weekends",
    icon: Snowflake,
    image: "https://images.unsplash.com/photo-1517299321609-52687d1bc55a?w=1200",
    overview:
      "Begin the year with intention. Our Winter Reset Weekends are designed for slowing down, journaling by the fire, gentle movement, and nourishing meals. Step off the ferry and into a calmer rhythm.",
    dates: ["January 9–11, 2026", "January 23–25, 2026"],
    included: [
      "Two nights island lodging",
      "All meals, coffee, tea & snacks",
      "Morning intention-setting & journaling sessions",
      "Gentle guided stretch / restorative movement",
      "Fireside evenings in cozy common spaces",
    ],
    itinerary: [
      { time: "Friday PM", text: "Ferry arrival, warm welcome, settle in, group dinner & fireside hello" },
      { time: "Saturday", text: "Slow breakfast, intention-setting, a bracing winter beach walk, free afternoon, hearty dinner" },
      { time: "Sunday AM", text: "Reflective morning, brunch, and a gentle send-off back to the mainland" },
    ],
    faq: [
      { q: "Do I need experience with journaling or movement?", a: "Not at all — every session is beginner-friendly and entirely optional." },
      { q: "Can I come solo?", a: "Absolutely. Many guests join on their own; it's a warm, welcoming group." },
    ],
  },
  {
    id: "february-couples-escape",
    month: "February",
    title: "Couples Island Escape Weekends",
    icon: Heart,
    image: "https://images.unsplash.com/photo-1551524559-8af4e6624178?w=1200",
    overview:
      "A romantic winter escape for two. Trade the everyday for candlelit dinners, quiet walks along a snow-dusted shore, and unhurried mornings together on a peaceful Maine island.",
    dates: ["February 13–15, 2026 (Valentine's weekend)", "February 20–22, 2026"],
    included: [
      "Two nights island lodging",
      "All meals, coffee, tea & snacks",
      "A special candlelit couples dinner",
      "Cozy common spaces & fireside evenings",
      "Optional in-room massage add-on (subject to availability)",
    ],
    itinerary: [
      { time: "Friday PM", text: "Arrival, welcome toast, and an intimate group dinner" },
      { time: "Saturday", text: "Leisurely breakfast, a couples' shore walk, free afternoon (massage available), candlelit dinner" },
      { time: "Sunday AM", text: "Slow morning, brunch, and a gentle departure" },
    ],
    faq: [
      { q: "Is the massage included?", a: "Massage is an optional add-on and may be available for an additional fee, subject to availability." },
      { q: "Can we request a private table for dinner?", a: "Yes — just let us know in your inquiry and we'll do our best to accommodate." },
    ],
  },
  {
    id: "march-reading-renewal",
    month: "March",
    title: "Reading & Renewal Weekends",
    icon: BookOpen,
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200",
    overview:
      "For book lovers and quiet souls. Bring a stack of books (or borrow from ours), claim a comfy chair by the window, and let the island's stillness do the rest.",
    dates: ["March 13–15, 2026", "March 20–22, 2026"],
    included: [
      "Two nights island lodging",
      "All meals, coffee, tea & snacks",
      "Curated reading nooks & a shared book swap",
      "Optional evening book discussion",
      "Fireside evenings & quiet common spaces",
    ],
    itinerary: [
      { time: "Friday PM", text: "Arrival, welcome, dinner, and a cozy reading-nook evening" },
      { time: "Saturday", text: "Slow breakfast, reading time, a refreshing walk, optional book discussion, dinner" },
      { time: "Sunday AM", text: "Final chapters, brunch, and farewell" },
    ],
    faq: [
      { q: "Do I have to join the book discussion?", a: "Never — it's completely optional. Reading in blissful silence is encouraged." },
      { q: "Will there be books available?", a: "Yes, we keep a shared shelf and host a book swap, but feel free to bring your own." },
    ],
  },
];

const COMING_SOON = [
  { name: "Mother-Daughter Retreats", icon: Heart },
  { name: "Cooking on a Maine Island Weekends", icon: Utensils },
  { name: "Murder Mystery Weekends", icon: Sparkles },
  { name: "Creative / Crafting Retreats", icon: BookOpen },
];

const RETREAT_OPTIONS = RETREATS.flatMap((r) =>
  r.dates.map((d) => `${r.month} — ${r.title} (${d})`)
);

export default function WinterRetreatsPage() {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    retreat: "",
    room_preference: "",
    guests: "1",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = (retreatValue) => {
    if (retreatValue) setForm((f) => ({ ...f, retreat: retreatValue }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/retreat-inquiry`, form);
      toast.success("Thank you! Your retreat inquiry has been sent — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", retreat: "", room_preference: "", guests: "1", message: "" });
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again or email chestnuthillinn@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <SEO
        title="Winter Retreats at Chestnut Hill Inn | Long Island, Maine"
        description="Cozy Friday-to-Sunday winter retreat weekends on Long Island, Maine. Winter Reset, Couples Island Escape, and Reading & Renewal weekends with lodging, meals, and themed activities."
      />

      {/* Header */}
      <header className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-[#1C1917]">
            Chestnut Hill Inn
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/about" className="text-sm text-[#78716C] hover:text-[#1C1917]">About</Link>
            <Link to="/beaches" className="text-sm text-[#78716C] hover:text-[#1C1917]">Beaches</Link>
            <Link to="/things-to-do" className="text-sm text-[#78716C] hover:text-[#1C1917]">Things to Do</Link>
            <Link to="/winter-retreats" className="text-sm text-[#1C1917] font-medium">Winter Retreats</Link>
            <Link to="/booking">
              <Button className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white">Book Now</Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=1920"
            alt="Snowy winter coast in Maine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end">
          <div className="max-w-6xl mx-auto px-6 pb-16 w-full">
            <div className="flex items-center gap-2 text-white/80 mb-4">
              <Snowflake size={20} />
              <span className="text-sm uppercase tracking-widest">Winter Retreat Series</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-4">
              Winter Retreats at<br />Chestnut Hill Inn
            </h1>
            <p className="text-white/90 text-lg max-w-2xl">
              Escape to a quiet Maine island this winter — cozy Friday-to-Sunday weekends centered on rest, connection, and good food.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button data-testid="hero-reserve-btn" onClick={() => scrollToForm("")} className="bg-white text-[#134E4A] hover:bg-stone-100">
                Reserve Your Spot
              </Button>
              <Button data-testid="hero-interest-btn" onClick={() => scrollToForm("")} variant="outline" className="bg-transparent border-white text-white hover:bg-white/10">
                Join the Winter Retreat Interest List
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-lg text-[#57534E] leading-relaxed">
            Chestnut Hill Inn is launching a special winter retreat series with cozy Friday-to-Sunday weekends
            centered around rest, connection, good food, and the beauty of Long Island in the off-season.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-[#78716C]">
            <span className="flex items-center gap-2"><CalendarDays size={16} className="text-[#134E4A]" /> Friday–Sunday · 2 nights</span>
            <span className="flex items-center gap-2"><Utensils size={16} className="text-[#134E4A]" /> All meals included</span>
            <span className="flex items-center gap-2"><Coffee size={16} className="text-[#134E4A]" /> Coffee, tea & snacks</span>
            <span className="flex items-center gap-2"><Waves size={16} className="text-[#134E4A]" /> Themed weekend activities</span>
          </div>
        </div>
      </section>

      {/* Retreats */}
      <section className="pb-8">
        <div className="max-w-6xl mx-auto px-6 space-y-20">
          {RETREATS.map((retreat, idx) => (
            <div key={retreat.id} id={retreat.id} className="scroll-mt-24">
              <div className={`grid lg:grid-cols-2 gap-10 items-start ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {/* Image + overview */}
                <div>
                  <div className="relative overflow-hidden rounded-lg mb-6">
                    <img src={retreat.image} alt={retreat.title} className="w-full h-64 object-cover" />
                    <div className="absolute top-4 left-4 bg-[#134E4A] text-white px-3 py-1.5 text-sm flex items-center gap-2">
                      <retreat.icon size={16} /> {retreat.month}
                    </div>
                  </div>
                  <h2 className="font-serif text-3xl text-[#1C1917] mb-3">{retreat.title}</h2>
                  <p className="text-[#57534E] leading-relaxed mb-6">{retreat.overview}</p>

                  {/* Dates */}
                  <div className="mb-6">
                    <h3 className="text-sm uppercase tracking-wide text-[#78716C] mb-2">Weekend Options</h3>
                    <div className="flex flex-wrap gap-2">
                      {retreat.dates.map((d) => (
                        <span key={d} className="bg-[#e6f2f1] text-[#134E4A] text-sm px-3 py-1.5 rounded-full">{d}</span>
                      ))}
                    </div>
                  </div>

                  <Button
                    data-testid={`reserve-${retreat.id}`}
                    onClick={() => scrollToForm(`${retreat.month} — ${retreat.title} (${retreat.dates[0]})`)}
                    className="bg-[#134E4A] hover:bg-[#0f3d3a] text-white"
                  >
                    Reserve Your Spot <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>

                {/* Details: included, itinerary, faq */}
                <div className="space-y-8">
                  <div className="bg-white border border-stone-200 p-6 rounded-lg">
                    <h3 className="font-medium text-[#1C1917] mb-4 flex items-center gap-2"><Check size={18} className="text-[#134E4A]" /> What&apos;s Included</h3>
                    <ul className="space-y-2">
                      {retreat.included.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-[#57534E]">
                          <Check size={16} className="text-[#134E4A] mt-0.5 shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-white border border-stone-200 p-6 rounded-lg">
                    <h3 className="font-medium text-[#1C1917] mb-4 flex items-center gap-2"><CalendarDays size={18} className="text-[#134E4A]" /> Sample Itinerary</h3>
                    <div className="space-y-3">
                      {retreat.itinerary.map((row) => (
                        <div key={row.time} className="flex gap-3 text-sm">
                          <span className="font-medium text-[#134E4A] w-24 shrink-0">{row.time}</span>
                          <span className="text-[#57534E]">{row.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white border border-stone-200 p-6 rounded-lg">
                    <h3 className="font-medium text-[#1C1917] mb-4">Frequently Asked</h3>
                    <div className="space-y-4">
                      {retreat.faq.map((f) => (
                        <div key={f.q}>
                          <p className="text-sm font-medium text-[#1C1917]">{f.q}</p>
                          <p className="text-sm text-[#78716C] mt-1">{f.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rooms & Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl text-[#1C1917] mb-3">Room & Pricing Options</h2>
            <p className="text-[#78716C] max-w-2xl mx-auto">
              Per-person pricing for the full Friday–Sunday weekend, including two nights lodging, meals, coffee/tea, snacks, and activities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ROOMS.map((room) => (
              <div key={room.name} className="bg-[#FAFAF9] border border-stone-200 p-6 rounded-lg flex flex-col">
                <div className="w-10 h-10 bg-[#e6f2f1] flex items-center justify-center rounded mb-4">
                  <room.icon size={20} className="text-[#134E4A]" />
                </div>
                <h3 className="font-serif text-xl text-[#1C1917]">{room.name}</h3>
                <p className="text-sm text-[#78716C] mt-1 mb-4 flex-1">{room.detail}</p>
                <div className="border-t border-stone-200 pt-4">
                  <span className="text-2xl font-semibold text-[#134E4A]">{room.price}</span>
                  <span className="text-sm text-[#78716C]"> / guest</span>
                  <p className="text-xs text-[#78716C] mt-1">{room.companion}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-[#78716C] mt-8">
            Massage may be available as an add-on. Have a question about rooms? Use the form below to request details.
          </p>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <span className="text-sm uppercase tracking-widest text-[#134E4A]">On the Horizon</span>
          <h2 className="font-serif text-3xl text-[#1C1917] mt-2 mb-10">Coming Soon</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {COMING_SOON.map((item) => (
              <div key={item.name} className="bg-white border border-dashed border-stone-300 p-6 rounded-lg">
                <div className="w-12 h-12 mx-auto bg-[#e6f2f1] flex items-center justify-center rounded-full mb-4">
                  <item.icon size={22} className="text-[#134E4A]" />
                </div>
                <p className="font-medium text-[#1C1917]">{item.name}</p>
                <span className="inline-block mt-3 text-xs uppercase tracking-wide text-[#78716C] bg-stone-100 px-2 py-1 rounded">Coming Soon</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section ref={formRef} className="py-16 bg-[#e6f2f1] scroll-mt-20" id="reserve">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl text-[#1C1917] mb-3">Reserve Your Spot</h2>
            <p className="text-[#57534E]">
              Tell us a little about what you&apos;re looking for and we&apos;ll be in touch with availability and details.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-stone-200 rounded-lg p-6 md:p-8 space-y-5" data-testid="retreat-inquiry-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="ret-name">Name *</Label>
                <Input id="ret-name" data-testid="retreat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" required />
              </div>
              <div>
                <Label htmlFor="ret-email">Email *</Label>
                <Input id="ret-email" data-testid="retreat-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" required />
              </div>
              <div>
                <Label htmlFor="ret-phone">Phone</Label>
                <Input id="ret-phone" data-testid="retreat-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
              </div>
              <div>
                <Label htmlFor="ret-guests">Number of Guests</Label>
                <Input id="ret-guests" data-testid="retreat-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} />
              </div>
            </div>

            <div>
              <Label>Retreat / Weekend of Interest</Label>
              <Select value={form.retreat} onValueChange={(v) => setForm({ ...form, retreat: v })}>
                <SelectTrigger data-testid="retreat-select"><SelectValue placeholder="Select a retreat weekend" /></SelectTrigger>
                <SelectContent>
                  {RETREAT_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                  <SelectItem value="Not sure yet / general interest">Not sure yet / general interest</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Room Preference</Label>
              <Select value={form.room_preference} onValueChange={(v) => setForm({ ...form, room_preference: v })}>
                <SelectTrigger data-testid="retreat-room"><SelectValue placeholder="Select a room type" /></SelectTrigger>
                <SelectContent>
                  {ROOMS.map((r) => (
                    <SelectItem key={r.name} value={`${r.name} (${r.price})`}>{r.name} — {r.price}</SelectItem>
                  ))}
                  <SelectItem value="No preference">No preference</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="ret-message">Questions / Comments</Label>
              <Textarea id="ret-message" data-testid="retreat-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Dietary needs, companion details, massage add-on, anything else..." />
            </div>

            <Button type="submit" data-testid="retreat-submit" disabled={submitting} className="w-full bg-[#134E4A] hover:bg-[#0f3d3a] text-white py-6">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {submitting ? "Sending..." : "Request Retreat Details"}
            </Button>
            <p className="text-xs text-center text-[#78716C]">
              Prefer to reach out directly? Email chestnuthillinn@gmail.com or call (603) 858-2272.
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] text-white py-12">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h3 className="font-serif text-2xl mb-2">Chestnut Hill Inn</h3>
          <p className="text-white/60 mb-4">209 Beach Ave, Long Island, ME</p>
          <div className="flex justify-center gap-4">
            <a href="https://www.facebook.com/chestnut.hill.inn.and.retreat.center" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="Facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
              </svg>
            </a>
            <a href="https://www.instagram.com/chestnuthillinn/" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="Instagram">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
